import corpusMd from "./chatbot-corpus.md?raw";

/**
 * Chatbot knowledge corpus.
 *
 * Source of truth: chatbot-corpus.md (edit there). This module just parses
 * the markdown at runtime — Vite hot-reload picks up md edits in dev,
 * production build inlines the md string into the bundle.
 *
 * Two consumers:
 *  1. Mock mode (current) — `searchCorpus(query, lang)` does keyword match.
 *  2. tuzi mode (next round) — `corpusToSystemPrompt(lang)` dumps the
 *     whole corpus as a system message for the LLM.
 */

export type CorpusEntry = {
  topic: string;
  keywords: { en: string[]; zh: string[] };
  text: { en: string; zh: string };
};

function splitCsv(s: string): string[] {
  // Accept both English `,` and Chinese `，` as separators.
  return s
    .split(/[,，]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * Read one labeled field out of a section body. Each field looks like:
 *   **Label:** value-on-same-or-multiple-lines
 * and ends at the next `**Something:**` label or end of section.
 */
function readField(section: string, label: string): string {
  // Match starts with `**<label>:**`, captures everything up to next `**X:**` or end-of-section.
  const re = new RegExp(
    `\\*\\*${label.replace(/[()]/g, "\\$&")}:\\*\\*\\s*([\\s\\S]*?)(?=\\n\\s*\\*\\*[^*\\n]+:\\*\\*|$)`,
    "i",
  );
  const m = section.match(re);
  return m ? m[1].trim() : "";
}

function parseCorpusMarkdown(md: string): CorpusEntry[] {
  // Split on H2 headers. First slice is the file preamble before any ##.
  const sections = md.split(/\n##\s+/);
  const entries: CorpusEntry[] = [];

  for (let i = 1; i < sections.length; i++) {
    const section = sections[i];
    const newlineIdx = section.indexOf("\n");
    const topic = (newlineIdx === -1 ? section : section.slice(0, newlineIdx)).trim();
    const body = newlineIdx === -1 ? "" : section.slice(newlineIdx + 1);

    const kwEnRaw = readField(body, "Keywords \\(EN\\)");
    const kwZhRaw = readField(body, "Keywords \\(ZH\\)");
    const textEn = readField(body, "EN");
    const textZh = readField(body, "ZH");

    // Skip malformed entries silently — keeps Chatbot resilient if md gets edited mid-flight.
    if (!topic || !textEn || !textZh) continue;

    entries.push({
      topic,
      keywords: {
        en: splitCsv(kwEnRaw),
        zh: splitCsv(kwZhRaw),
      },
      text: { en: textEn, zh: textZh },
    });
  }

  return entries;
}

export const corpus: CorpusEntry[] = parseCorpusMarkdown(corpusMd);

/**
 * Pick the best-matching entries for a user message (mock mode).
 * Lowercase substring keyword match; ranks by # of matched keywords.
 */
export function searchCorpus(
  query: string,
  lang: "en" | "zh",
  limit = 2,
): CorpusEntry[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  const scored = corpus
    .map((entry) => {
      const kws = entry.keywords[lang]?.length ? entry.keywords[lang] : entry.keywords.en;
      const hits = kws.filter((k) => q.includes(k.toLowerCase())).length;
      return { entry, hits };
    })
    .filter((x) => x.hits > 0)
    .sort((a, b) => b.hits - a.hits);
  return scored.slice(0, limit).map((s) => s.entry);
}

/**
 * Flatten the whole corpus to a system-prompt-friendly string.
 * Pass as the system message when wiring tuzi so the LLM answers
 * from OUR copy rather than its own training data.
 */
export function corpusToSystemPrompt(lang: "en" | "zh"): string {
  const intro =
    lang === "zh"
      ? [
          "你是 OranAI 官网的客服助手。",
          "",
          "回答风格：",
          "- 用自然、口语化的中文回复，像跟同事聊天一样",
          "- 简短优先 —— 通常 1-3 句即可；只有用户明确问「列一下」或要详细对比时，才用 1-2 个简单要点",
          "- 不要用 markdown 标题、不要用粗体/斜体来强调（直接说就行）",
          "- 提到 URL 时直接写出网址，不要用 <url> 或 [文字](url) 这种格式",
          "- 仅基于下面的知识库回答；知识库没覆盖的，如实说「暂时没这部分信息」并建议邮件 hey@photog.art",
          "",
          "知识库：",
          "",
        ].join("\n")
      : [
          "You are OranAI's website assistant.",
          "",
          "Response style:",
          "- Reply in natural, conversational English — like chatting with a colleague",
          "- **Keep it short** — usually 1-3 sentences. Only use a short bulleted list (1-2 bullets) if the user explicitly asks to compare or list options",
          "- Do NOT use markdown headers, bold, or italics for emphasis — just say it",
          "- When mentioning a URL, write the plain URL — no `<url>` brackets, no `[text](url)` markdown links",
          "- Answer ONLY from the knowledge base below. If something isn't covered, say so honestly and suggest emailing hey@photog.art.",
          "",
          "Knowledge base:",
          "",
        ].join("\n");
  const body = corpus.map((e) => `[${e.topic}] ${e.text[lang]}`).join("\n\n");
  return intro + body;
}
